import React, { useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useQueryClient } from "@tanstack/react-query";
import { useDispatch, useSelector } from "react-redux";
import { EndPoints } from "../../Constants/endpoints";
import { useToast } from "../../Services/ToastContext";
import useEditPrescription from "../../api/Prescription/useEditPrescription";
import { RootState } from "../../redux/store";
import { clearSelectedPrescription } from "../../redux/reducers/selectedPrescriptionSlice";
 import { cleanFormData } from "./hooks/usePrescriptionSubmit";
import { PrescriptionBaseForm } from "./components";

const EditPrescriptionOfPatient: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { showToast } = useToast();
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { PatientId } = useParams();
  const location = useLocation();

  const handleBack = () => {
    navigate(location.state?.from || -1);
  };

  const [localInitialData, setLocalInitialData] = useState<any>(undefined);

  const selectedTemplate = useSelector(
    (state: RootState) => state.selectedPrescription.selectedTemplate,
  );

  const { mutate, isPending } = useEditPrescription();

  useEffect(() => {
    if (selectedTemplate) {
      const drugsData =
        selectedTemplate.Drugs?.map((drug: any, index: number) => {
          if (!drug.PackageId && drug.Name) {
            return {
              Name: drug.Name,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
                ? drug.Days.map((day: any) => day.Id)
                : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
              _tempId: `patient_edit_${index}_${drug.Name}`,
            };
          } else {
            return {
              PackageId: drug.PackageId,
              DrugName: drug.DrugName,
              Frequency: drug.FrequencyCode || "",
              SelectedDays: drug.Days
                ? drug.Days.map((day: any) => day.Id)
                : [],
              Meal: drug.MealCode || "",
              Sleep: drug.Sleep || false,
              Times: drug.Times,
              Duration: drug.Duration,
              Instructions: drug.Instructions || "",
              DosageNum: drug.DosageNum,
              DosageUnit: drug.DosageUnit,
            };
          }
        }) || [];

      setLocalInitialData({
        Case: selectedTemplate.Case || "",
        Drugs: drugsData,
      });
    }
  }, [selectedTemplate]);

  const handleSubmit = (data: any) => {
    const cleanedData = cleanFormData(data);
    const id = Number(selectedTemplate?.Id);

    mutate(
      { id, data: cleanedData },
      {
        onSuccess: () => {
          showToast(t("prescription_updated_successfully"), "success");
          dispatch(clearSelectedPrescription());

          queryClient.refetchQueries({
            queryKey: [EndPoints.GetPrescriptions],
          });
          queryClient.refetchQueries({
            queryKey: [`${EndPoints.GetPrescriptionToPatient}/${PatientId}`],
          }); 
          navigate(`/patients_list/${PatientId}/prescriptions_by_me`);
        },
        onError: (error: any) => { 
          showToast(error.message || t("error_adding_prescription"), "error");
        },
      },
    );
  };

  const handleDiscard = () => {
    dispatch(clearSelectedPrescription());
    handleBack();
  };

  return (
    <PrescriptionBaseForm
      key={localInitialData?.Drugs?.length}
      patientId={PatientId}
      initialData={localInitialData}
      onSubmit={handleSubmit}
      onDiscard={handleDiscard}
      config={{
        title: t("edit_prescription"),
        submitButtonText: t("save"),
        caseLabel: t("case_name"),
        showPatientInfo: true,
        maxInstructionsLength: 500,
      }}
      isLoading={isPending}
    />
  );
};

export default EditPrescriptionOfPatient;
